import React, { useEffect, useState } from 'react'
import {
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Checkbox,
  CircularProgress,
  Box,
  Select,
  InputLabel,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'

// DTOs based on requirements
interface RegistrationSubmitDto {
  salutation: 'MR' | 'MRS' | 'DIVERSE'
  firstName: string
  lastName: string
  email: string
  phone?: string | null
  address?: string | null
  postalCode?: string | null
  city?: string | null
  country?: string | null // CountryCode
  eventId: string
  eventCostId: string
  dietId: string | null
  promoCode: string | null
  notes: string | null
  invoiceType: 'CONTACT' | 'COMPANY'
  birthDate: string | null
  mobilePhone: string | null
  department: string | null
  title: string | null
  companyName: string | null
  contactConsent: boolean
}

interface AddressDto {
  address: string
  postalCode: string
  city: string
  country?: string | null
}

interface ContactDto {
  id: string
  salutation: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  birthDate: string | null
}

interface CompanyDto {
  id: string
  name: string
  address: AddressDto | null
  billingAddress: AddressDto | null
}

interface EventPartDto {
  id: string
  name: string
  date: string | null
  timeFrom: string | null
  timeUntil: string | null
}

interface ParticipationDto {
  id: string
  eventId: string
  eventName: string
  registrationDate: string | null
  eventCostLabel: string | null
  promoCode: string | null
  dietName: string | null
  notes: string | null
  invoiceType: string
  eventParts: EventPartDto[]
}

interface MembershipResultDto {
  id: string
  membershipNumber: string
  kind: string
  status: string
}

enum RegistrationError {
  ALREADY_REGISTERED = 'ALREADY_REGISTERED',
  NAME_MISMATCH = 'NAME_MISMATCH',
  INVALID_PROMO_CODE = 'INVALID_PROMO_CODE',
  EVENT_NOT_FOUND = 'EVENT_NOT_FOUND',
  COST_OPTION_NOT_FOUND = 'COST_OPTION_NOT_FOUND',
  COST_OPTION_INVALID = 'COST_OPTION_INVALID',
  MEMBERSHIP_TYPE_NOT_FOUND = 'MEMBERSHIP_TYPE_NOT_FOUND',
  INTERNAL_ERROR = 'INTERNAL_ERROR'
}

interface RegistrationResultDto {
  participationId?: string | null
  contactId?: string | null
  updatedAddress?: AddressDto | null
  newMembership?: MembershipResultDto | null
  updatedMembership?: MembershipResultDto | null
  contact?: ContactDto | null
  company?: CompanyDto | null
  participation?: ParticipationDto | null
  eventCost: number
  error?: RegistrationError | null
  errorMessage?: string | null
}

interface EventCostDto {
  id: string
  label: string
  participationFee: number | null
  validFrom: string | null
  validUntil: string | null
  promoCodeRequired?: boolean
}

interface DietDto {
  id: string
  name: string
}

interface UpcomingEventDto {
  id: string
  name: string
  dateFrom: string
  dateUntil: string
  location: string
  eventPeriod: string
  participationInstructions: string | null
  diets: DietDto[]
  eventCostOptions: EventCostDto[]
}

const EventRegistration: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [upcomingEvent, setUpcomingEvent] = useState<UpcomingEventDto | null>(null)
  const [eventCosts, setEventCosts] = useState<EventCostDto[]>([])
  const [diets, setDiets] = useState<DietDto[]>([])
  const [registrationResult, setRegistrationResult] = useState<RegistrationResultDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Form State
  const [formData, setFormData] = useState({
    // Step 1
    email: '',
    salutation: 'MR',
    lastName: '',
    firstName: '',
    companyName: '',
    jobTitle: '',
    title: '',
    birthDate: '',
    website: '',
    // Step 2
    invoiceType: 'COMPANY',
    address: '',
    city: '',
    postalCode: '',
    phoneMobile: '',
    phone: '',
    // Step 3
    eventCostId: '',
    dietId: 'KONVENTIONELL',
    promoCode: '',
    remarks: '',
    contactConsent: 'NO',
    privacyAccepted: false,
  })

  const basePath = Env.basePath
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)

  useEffect(() => {
    // Initial fetch for upcoming events
    fetch(`${basePath}/api/public/events/upcoming`, {
      method: 'GET',
      headers: requestHeaders,
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('Upcoming events raw data:', data)
        // Check if data is an array or an object
        const eventData: UpcomingEventDto | null = Array.isArray(data) ? data[0] : data
        if (eventData) {
          setUpcomingEvent(eventData)
          if (eventData.eventCostOptions) {
            setEventCosts(eventData.eventCostOptions)
          }
          if (eventData.diets) {
            setDiets(eventData.diets)
          }
        }
      })
      .catch((err) => console.error('Error fetching upcoming events:', err))
  }, [])

  const handleNext = () => {
    if (activeStep === 2) {
      // Final Submit: Call check-contact as requested
      submitFinalRegistration()
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string, value: unknown }>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name as string]: value }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.checked }))
  }

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return (
          formData.firstName.trim() !== '' &&
          formData.lastName.trim() !== '' &&
          formData.email.trim() !== '' &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
          formData.companyName.trim() !== '' &&
          formData.jobTitle.trim() !== '' &&
          formData.birthDate.trim() !== ''
        )
      case 1:
        return (
          formData.address.trim() !== '' &&
          formData.postalCode.trim() !== '' &&
          formData.city.trim() !== '' &&
          formData.phoneMobile.trim() !== ''
        )
      case 2:
        return formData.eventCostId !== '' && formData.contactConsent !== '' && formData.privacyAccepted
      default:
        return true
    }
  }

  const submitFinalRegistration = () => {
    if (!upcomingEvent) return

    // Honeypot check
    if (formData.website) {
      console.warn('Spam detected via Honeypot')
      // Silently fail or redirect to success page without actually doing anything
      // In this case, we just show the success page to not give the bot any feedback
      setActiveStep(3)
      return
    }

    setLoading(true)
    const dto: RegistrationSubmitDto = {
      salutation: formData.salutation as 'MR' | 'MRS' | 'DIVERSE',
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phoneMobile || null,
      address: formData.address || null,
      postalCode: formData.postalCode || null,
      city: formData.city || null,
      country: 'DE',
      eventId: upcomingEvent.id,
      eventCostId: formData.eventCostId,
      dietId: formData.dietId === 'KONVENTIONELL' ? null : formData.dietId,
      promoCode: formData.promoCode || null,
      notes: formData.remarks || null,
      invoiceType: formData.invoiceType as 'CONTACT' | 'COMPANY',
      birthDate: formData.birthDate || null,
      mobilePhone: formData.phoneMobile || null,
      department: formData.jobTitle || null,
      title: formData.title || null,
      companyName: formData.companyName || null,
      contactConsent: formData.contactConsent === 'YES',
    }

    fetch(`${basePath}/api/public/events/register`, {
      method: 'POST',
      headers: requestHeaders,
      body: JSON.stringify(dto),
    })
      .then(async (res) => {
        const data = await res.json()
        if (!res.ok || data.error) {
          let errorMsg = data.errorMessage
          if (data.error === RegistrationError.ALREADY_REGISTERED && !errorMsg) {
            errorMsg = 'Du bist bereits für dieses Event registriert.'
          }
          throw new Error(errorMsg || data.error || 'Registration failed')
        }
        return data
      })
      .then((data: RegistrationResultDto) => {
        setRegistrationResult(data)
        console.log('Registration successful, result:', data)
        setActiveStep(3)
      })
      .catch((err) => {
        console.error('Error during registration:', err)
        setError(err.message || 'Bei der Anmeldung ist ein Fehler aufgetreten, bitte wenden Sie sich an das Markencamp Team!')
      })
      .finally(() => setLoading(false))
  }

  const steps = ['Kontaktdaten', 'Rechnungsdetails', 'Teilnahmeoptionen']

  const getCancellationConditions = () => {
    if (!upcomingEvent) return 'Eine Stornierung der Anmeldung ist bis 31.12. des Vorjahres kostenlos möglich. Bei einer Stornierung bis 01.02. fällt eine Stornogebühr in Höhe von 50% der Teilnahmegebühr an, danach erhöht sich die Stornogebühr auf 100% der Teilnahmegebühr.'

    const eventDate = new Date(upcomingEvent.dateFrom)
    const year = eventDate.getFullYear()
    const lastYear = year - 1

    return `Eine Stornierung der Anmeldung ist bis 31.12.${lastYear} kostenlos möglich. Bei einer Stornierung bis 01.02.${year} fällt eine Stornogebühr in Höhe von 50% der Teilnahmegebühr an, danach erhöht sich die Stornogebühr auf 100% der Teilnahmegebühr.`
  }

  const getMemberPriceCondition = () => {
    if (!upcomingEvent) return 'Voraussetzung für den Mitgliedspreis zur Teilnahme am MARKENCAMP ist die Mitgliedschaft im MARKENCAMP e.V. vor Beginn der Veranstaltung.'

    const eventDate = new Date(upcomingEvent.dateFrom)
    const day = eventDate.getDate().toString().padStart(2, '0')
    const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    const month = monthNames[eventDate.getMonth()]
    const year = eventDate.getFullYear()

    return `Voraussetzung für den Mitgliedspreis zur Teilnahme am MARKENCAMP ist die Mitgliedschaft im MARKENCAMP e.V. vor Beginn der Veranstaltung am ${day}. ${month} ${year}.`
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component='fieldset'>
                <FormLabel component='legend'>Anrede</FormLabel>
                <RadioGroup row name='salutation' value={formData.salutation} onChange={handleChange}>
                  <FormControlLabel value='MR' control={<Radio />} label='Herr' />
                  <FormControlLabel value='MS' control={<Radio />} label='Frau' />
                  <FormControlLabel value='DIVERSE' control={<Radio />} label='Divers' />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label='Titel (optional)'
                name='title'
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4.5}>
              <TextField
                required
                fullWidth
                label='Vorname'
                name='firstName'
                value={formData.firstName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4.5}>
              <TextField
                required
                fullWidth
                label='Nachname'
                name='lastName'
                value={formData.lastName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Email'
                name='email'
                type='email'
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Firma'
                name='companyName'
                value={formData.companyName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Abteilung'
                name='jobTitle'
                value={formData.jobTitle}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Geburtsdatum'
                name='birthDate'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={formData.birthDate}
                onChange={handleChange}
              />
            </Grid>
            {/* Honeypot field - hidden for humans */}
            <Grid item xs={12} sx={{ display: 'none' }}>
              <TextField
                fullWidth
                label='Name (bitte leer lassen)'
                name='website'
                value={formData.website}
                onChange={handleChange}
                autoComplete='off'
                tabIndex={-1}
              />
            </Grid>
          </Grid>
        )
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl component='fieldset'>
                <FormLabel component='legend'>Rechnung an</FormLabel>
                <RadioGroup row name='invoiceType' value={formData.invoiceType} onChange={handleChange}>
                  <FormControlLabel value='COMPANY' control={<Radio />} label='Firma' />
                  <FormControlLabel value='CONTACT' control={<Radio />} label='Privat' />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Anschrift'
                name='address'
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                label='PLZ'
                name='postalCode'
                value={formData.postalCode}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                required
                fullWidth
                label='Ort'
                name='city'
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label='Mobiltelefon'
                name='phoneMobile'
                value={formData.phoneMobile}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Festnetz (optional)'
                name='phone'
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        )
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id='event-cost-label'>Teilnahmeoption</InputLabel>
                <Select
                  labelId='event-cost-label'
                  name='eventCostId'
                  value={formData.eventCostId}
                  label='Teilnahmeoption'
                  onChange={handleChange as any}
                >
                  {eventCosts.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label} ({option.participationFee},- zzgl. MwSt.)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ marginTop: '20px' }}><b>Voraussetzungen Tarife Vereinsmitglied / Unternehmensmitglied:</b></Typography>
              <Typography variant='body2' sx={{ marginBottom: '20px', display: 'block', mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                {upcomingEvent?.participationInstructions || 'Bei der Auswahl der Tarife Vereinsmitglied / Unternehmensmitglied setzen wir eine bereits bestehende Mitgliedschaft voraus. Solltest Du diesen Tarif wählen, ohne Mitglied zu sein, gehen wir davon aus, dass du gern als neues Mitglied unserem Verein beitreten möchtest. Wir senden dir dann automatisch den Antrag auf Mitgliedschaft zu. Wir bitten dich daher diese Auswahl sorgfältig zu tätigen.'}
              </Typography>
            </Grid>
            {eventCosts.find(c => c.id === formData.eventCostId)?.promoCodeRequired && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Promo-Code'
                  name='promoCode'
                  value={formData.promoCode}
                  onChange={handleChange}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id='diet-label'>Catering Präferenz</InputLabel>
                <Select
                  labelId='diet-label'
                  name='dietId'
                  value={formData.dietId}
                  label='Catering Präferenz'
                  onChange={handleChange as any}
                  displayEmpty
                >
                  <MenuItem key='KONVENTIONELL' value='KONVENTIONELL'>
                    Konventionell
                  </MenuItem>
                  {diets.map((diet) => (
                    <MenuItem key={diet.id} value={diet.id}>
                      {diet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label='Anmerkungen'
                name='remarks'
                value={formData.remarks}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormControl fullWidth required sx={{ mb: 1 }}>
                <InputLabel id='contact-consent-label' shrink={formData.contactConsent !== '' || formData.contactConsent === ''}>Einwilligung zur Kontaktaufnahme durch den MARKENCAMP e.V. *</InputLabel>
                <Select
                  labelId='contact-consent-label'
                  name='contactConsent'
                  value={formData.contactConsent}
                  label='Einwilligung zur Kontaktaufnahme durch den MARKENCAMP e.V. *'
                  onChange={handleChange as any}
                  displayEmpty
                >
                  <MenuItem value='' disabled>
                    <em>Bitte wählen...</em>
                  </MenuItem>
                  <MenuItem value='YES'>Ja, ich möchte kontaktiert werden</MenuItem>
                  <MenuItem value='NO'>Nein, ich möchte nicht kontaktiert werden</MenuItem>
                </Select>
              </FormControl>
              <Typography variant='body2' sx={{ mb: 3 }}>
                Wenn Du hier "Nein" anklickst, können wir Dir leider keine weiteren Informationen und updates zu dieser Veranstaltung zukommen lassen.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                Verarbeitung personenbezogener Daten *
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name='privacyAccepted'
                    checked={formData.privacyAccepted}
                    onChange={handleCheckboxChange}
                  />
                }
                label={
                  <Typography variant='body2'>
                    Hiermit erteile ich die Zustimmung (bis auf Widerruf) gemäß Art. 6 Abs. 1a DS-GVO für die Verarbeitung meiner personenbezogenen Daten durch den Verein MARKENCAMP e.V. für die oben genannte Veranstaltung zur Vertragserfüllung im Sinne der organisatorischen Abwicklung der Veranstaltung. Ich willige ein, dass insbesondere die hier aufgenommenen Bild- und Tonaufnahmen zur Erfüllung der satzungsgemäßen Aufgaben, zu organisatorischen Zwecken sowie zur Veröffentlichung in den Vereins-Medien (Homepage & Newsletter) sowie den sozialen Medien des Vereins (Facebook, Twitter & Instagram) verarbeitet werden. <br />Eine Weitergabe der Daten an Dritte findet nicht statt. Weitere Informationen findest Du in unseren <a href='https://www.markencamp.com/datenschutzerklaerung/' target='_blank' rel='noopener noreferrer'>Datenschutzhinweisen</a>.
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        )
      case 3:
        return (
          <Box textAlign='center' py={4}>
            <Typography variant='h5' gutterBottom>Vielen Dank für Ihre Anmeldung!</Typography>
            <Typography variant='body1' sx={{ mb: 4 }}>Wir haben Ihre Daten erhalten und werden uns in Kürze bei Ihnen melden.</Typography>

            {registrationResult && (
              <Box sx={{ mt: 4, mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#ffffff', textAlign: 'left' }}>
                <Typography variant='h6' gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>Zusammenfassung Deiner Registrierung:</Typography>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {/* Event & Teilnahme */}
                  <Grid item xs={12}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 0.5, mb: 1 }}>Teilnahme-Details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='subtitle2' color='text.secondary'>Event:</Typography>
                        <Typography variant='body1'>{registrationResult.participation?.eventName || upcomingEvent?.name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='subtitle2' color='text.secondary'>Tarif:</Typography>
                        <Typography variant='body1'>{registrationResult.participation?.eventCostLabel || 'Standard'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant='subtitle2' color='text.secondary'>Kosten:</Typography>
                        <Typography variant='body1'>{registrationResult.eventCost || 0} € (zzgl. MwSt.)</Typography>
                      </Grid>
                      {registrationResult.participation?.dietName && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant='subtitle2' color='text.secondary'>Catering:</Typography>
                          <Typography variant='body1'>{registrationResult.participation.dietName}</Typography>
                        </Grid>
                      )}
                      {(registrationResult.participation?.notes || formData.remarks) && (
                        <Grid item xs={12}>
                          <Typography variant='subtitle2' color='text.secondary'>Anmerkungen:</Typography>
                          <Typography variant='body1'>{registrationResult.participation?.notes || formData.remarks}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Kontaktperson */}
                  {registrationResult.contact && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 0.5, mb: 1 }}>Teilnehmer</Typography>
                      <Typography variant='body1'>{registrationResult.contact.firstName} {registrationResult.contact.lastName}</Typography>
                      <Typography variant='body2' color='text.secondary'>{registrationResult.contact.email}</Typography>
                      {registrationResult.contact.phone && (
                        <Typography variant='body2' color='text.secondary'>{registrationResult.contact.phone}</Typography>
                      )}
                    </Grid>
                  )}

                  {/* Unternehmen / Rechnungsadresse */}
                  {(registrationResult.company || registrationResult.updatedAddress) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 0.5, mb: 1 }}>Rechnungsanschrift</Typography>
                      {registrationResult.company && (
                        <Typography variant='body1' sx={{ fontWeight: 'medium' }}>{registrationResult.company.name}</Typography>
                      )}
                      {registrationResult.updatedAddress ? (
                        <Typography variant='body2'>
                          {registrationResult.updatedAddress.address}<br />
                          {registrationResult.updatedAddress.postalCode} {registrationResult.updatedAddress.city}
                        </Typography>
                      ) : registrationResult.company?.billingAddress ? (
                        <Typography variant='body2'>
                          {registrationResult.company.billingAddress.address}<br />
                          {registrationResult.company.billingAddress.postalCode} {registrationResult.company.billingAddress.city}
                        </Typography>
                      ) : null}
                    </Grid>
                  )}

                  {/* Mitgliedschaft */}
                  {(registrationResult.newMembership || registrationResult.updatedMembership) && (
                    <Grid item xs={12}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 0.5, mb: 1 }}>Mitgliedschaft</Typography>
                      <Grid container spacing={2}>
                        {registrationResult.newMembership && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' color='text.secondary'>Neue Mitgliedschaft:</Typography>
                            <Typography variant='body1' sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {registrationResult.newMembership.kind === 'COMPANY' ? 'Unternehmensmitgliedschaft' : 'Einzelmitgliedschaft'}
                            </Typography>
                            <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 1 }}>Mitgliedsnummer:</Typography>
                            <Typography variant='body1' sx={{ fontWeight: 'bold', color: 'primary.main' }}>{registrationResult.newMembership.membershipNumber}</Typography>
                            <Typography variant='caption' sx={{ display: 'block', mt: 0.5 }}>Willkommen im MARKENCAMP e.V.!</Typography>
                          </Grid>
                        )}
                        {registrationResult.updatedMembership && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant='subtitle2' color='text.secondary'>Mitgliedschaft:</Typography>
                            <Typography variant='body1'>
                              {registrationResult.updatedMembership.kind === 'COMPANY' ? 'Unternehmensmitgliedschaft' : 'Einzelmitgliedschaft'}
                            </Typography>
                            <Typography variant='subtitle2' color='text.secondary' sx={{ mt: 1 }}>Mitgliedsnummer:</Typography>
                            <Typography variant='body1' sx={{ fontWeight: 'medium' }}>{registrationResult.updatedMembership.membershipNumber}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}

                  {/* Event-Abschnitte (falls vorhanden) */}
                  {registrationResult.participation?.eventParts && registrationResult.participation.eventParts.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', borderBottom: '1px solid #eee', pb: 0.5, mb: 1 }}>Programm-Hinweise</Typography>
                      <Grid container spacing={1}>
                        {registrationResult.participation.eventParts.map((part) => (
                          <Grid item xs={12} key={part.id}>
                            <Typography variant='body2'>
                              <strong>{part.name}</strong>
                              {part.date && ` am ${new Date(part.date).toLocaleDateString('de-DE')}`}
                              {(part.timeFrom || part.timeUntil) && ` (${part.timeFrom || ''}${part.timeFrom && part.timeUntil ? ' - ' : ''}${part.timeUntil || ''} Uhr)`}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 6, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#f9f9f9' }}>
              <Typography variant='h6' gutterBottom>Mit der Deutschen Bahn zum MARKENCAMP</Typography>
              <Typography variant='body2' sx={{ mb: 2 }}>
                Mit der Deutschen Bahn ab 49,50 Euro zu MARKENCAMP & MARKENLOUNGE.
              </Typography>
              <Button
                variant='contained'
                color='secondary'
                href='https://www.markencamp.com/anreise/'
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  backgroundColor: 'secondary.main',
                  color: '#fff',
                  fontWeight: 'bold',
                  padding: '12px 32px',
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  },
                }}
              >
                JETZT BUCHEN
              </Button>
            </Box>
          </Box>
        )
      default:
        return 'Unbekannter Schritt'
    }
  }

  return (
    <Container maxWidth={false} sx={{ mt: 8, mb: 8, width: '100%', ml: 'auto', mr: 'auto', maxWidth: '75em' }}>
      {upcomingEvent && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant='h3' gutterBottom sx={{ fontWeight: 'bold' }}>
            {upcomingEvent.name}
          </Typography>
          <Typography variant='h6' color='text.secondary'>
            {upcomingEvent.eventPeriod} | {upcomingEvent.location}
          </Typography>
        </Box>
      )}

      <Typography variant='h4' align='center' sx={{ mb: 6 }}>
        Melde Dich jetzt an und werde Teil des MARKENCAMP Netzwerks!
      </Typography>

      {activeStep < 3 && (
        <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '1.1rem' } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Box component='form' noValidate>
        {renderStepContent(activeStep)}

        {activeStep < 3 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Zurück
              </Button>
            )}
            <Button
              variant='contained'
              color={activeStep === 2 ? 'primary' : 'primary'}
              onClick={handleNext}
              disabled={loading || !isStepValid()}
            >
              {loading ? <CircularProgress size={24} /> : activeStep === 2 ? 'KOSTENPFLICHTIG ANMELDEN' : 'Weiter'}
            </Button>
          </Box>
        )}
      </Box>

      {activeStep < 3 && (
        <Box sx={{ mt: 4, color: 'text.secondary' }}>
          <Typography variant='body2' sx={{ display: 'block', mb: 1 }}>
            <strong>Stornobedingungen:</strong> {getCancellationConditions()}
          </Typography>
          <Typography variant='body2' sx={{ display: 'block', mb: 1 }}>
            <strong>Mitgliedspreis:</strong> {getMemberPriceCondition()}
          </Typography>
          <Typography variant='body2' sx={{ display: 'block', mb: 1 }}>
            <strong>Ticketübertragung:</strong> Für den Fall, dass ein angemeldeter Mitarbeiter:in das Unternehmen verlässt, kann das Ticket auf eine andere Person aus dem Unternehmen übertragen werden.
          </Typography>
          <Typography variant='body2' sx={{ display: 'block' }}>
            Jeder Besucher bzw. Teilnehmer einer Veranstaltung des MARKENCAMP e.V. willigt mit seiner Anmeldung darin ein, dass der Veranstalter im Rahmen der Veranstaltung, ohne zur Zahlung einer Vergütung verpflichtet zu sein, berechtigt ist, Bild-, Video- und Tonaufnahmen der Besucher zu erstellen und/oder durch Dritte erstellen zu lassen, diese zu vervielfältigen, zu senden und in jeglichen audiovisuellen Medien zu nutzen und/oder durch Dritte bzw. Partner vervielfältigen, senden und nutzen zu lassen. Diese Rechte gelten zeitlich unbeschränkt und weltweit.
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 10 }}>
        <Divider sx={{ mb: 6 }} />
        <Typography variant='h4' align='center' gutterBottom sx={{ fontWeight: 'bold' }}>
          F.A.Q
        </Typography>
        <Typography variant='subtitle1' align='center' sx={{ mb: 4, color: 'text.secondary' }}>
          Deine Fragen, unsere Antworten.
        </Typography>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Was beinhaltet der Preis?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Teilnahme am Kongress. Alle Vorträge. Teilnahme an einem Workshop. Get-together. Netzwerkparty. Jede Menge hochwertigen Content, Spaß und gute Leute.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Muss ich die Anreise und das Hotel gesondert buchen und bezahlen?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Ja, das solltest Du.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Wieviel Teilnehmer erwartet ihr?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Die Teilnehmerzahl ist auf 200 limitiert.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Muss ich mich gleich anmelden?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Ja, denn das MARKENCAMP begeistert und wird schnell ausgebucht sein.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Wie werde ich Mitglied im MARKENCAMP e.V.?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Den Aufnahmeantrag kannst Du Dir <a href='https://www.markencamp.com/wp-content/uploads/2015/02/Mitgliedsantrag-2025.pdf' target='_blank' rel='noopener noreferrer' style={{ color: '#d32f2f', textDecoration: 'none' }}>hier</a> herunterladen und an <a href='mailto:ng@markencamp.com' style={{ color: '#d32f2f', textDecoration: 'none' }}>ng@markencamp.com</a> übermitteln.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Was kostet die Mitgliedschaft im MARKENCAMP e.V.?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Der Jahresbeitrag liegt bei € 199,-. Die Aufnahmegebühr liegt bei einmalig € 49,-.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid #f0f0f0' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Was bekomme ich für meinem Mitgliedsbeitrag?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Du wirst Teil des bundesweiten Netzwerks und erhältst Zugang zur Plattform für Markenmacher. Ausserdem kannst Du am Markencamp zu Vorteilspreisen teilnehmen. Dieser findet einmal pro Jahr an unterschiedlichen Standorten in Deutschland statt. Darüber hinaus erhältst Du weitere Vorteilsangebote, wie z.B. kostenlose Teilnahme an den MARKENLOUNGES, unserem regionalen Netzwerkformat, das regelmäßig in verschiedenen Städten im Bundesgebiet stattfindet und ein Abonnement der Fachzeitschrift w&v, Vorteilspreise zu Bahntickets und mehr.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Dialog
        open={!!error}
        onClose={() => setError(null)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          Anmeldung fehlgeschlagen
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {error}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setError(null)} autoFocus>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default EventRegistration
